import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { DollarSign, History, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmt = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);

// Admin pay-rate workflow: current rate (read-only) + Change Pay Rate modal +
// durable Pay Rate History. Never overwrites the prior rate — every change is a
// new effective-dated record via POST /admin/employees/{id}/pay-rate.
const PayRateManager = ({ employeeId, canEdit, canView }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ new_rate: '', effective_date: '', reason: 'Annual Raise', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId) return;
    try {
      const { data } = await axios.get(`${API}/admin/employees/${employeeId}/pay-rate-history`, { withCredentials: true });
      setData(data);
    } catch (e) {
      if (e.response?.status !== 403) console.error(e);
      setData({ restricted: e.response?.status === 403 });
    }
  }, [employeeId]);

  useEffect(() => { if (canView) load(); }, [load, canView]);

  const openModal = () => {
    setForm({
      new_rate: data?.current_rate ?? '',
      effective_date: new Date().toISOString().slice(0, 10),
      reason: 'Annual Raise',
      notes: '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (form.new_rate === '' || Number(form.new_rate) < 0) { toast.error('Enter a valid new rate'); return; }
    if (!form.effective_date) { toast.error('Choose an effective date'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/pay-rate`, {
        new_rate: Number(form.new_rate),
        effective_date: form.effective_date,
        reason: form.reason,
        notes: form.notes || null,
      }, { withCredentials: true });
      toast.success('Pay rate change recorded');
      setOpen(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = (d && typeof d === 'object') ? d.message : (d || 'Failed to save pay-rate change');
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!canView) return null;
  if (data?.restricted) {
    return (
      <div data-testid="pay-rate-restricted" className="bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-[#A3A3A3] text-sm flex items-center gap-2">
        <span aria-label="restricted">🔒</span> Restricted
      </div>
    );
  }
  const hist = data?.history || [];
  const reasons = data?.reasons || ['Annual Raise', 'Promotion', 'Performance Increase', 'Market Adjustment', 'Correction', 'Other'];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <DollarSign className="w-4 h-4 text-[#D4AF37] shrink-0" />
          <div className="min-w-0">
            <p data-testid="pay-rate-current" className="text-white font-bold">{money(data?.current_rate)}<span className="text-[#A3A3A3] font-normal text-sm">/hr</span></p>
            {data?.current_effective_date && <p className="text-[#737373] text-[11px]">Effective {fmt(data.current_effective_date)}</p>}
          </div>
        </div>
        {canEdit && (
          <Button data-testid="change-pay-rate-btn" size="sm" onClick={openModal} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] shrink-0">
            <TrendingUp className="w-4 h-4 mr-1" /> Change Pay Rate
          </Button>
        )}
      </div>

      {hist.length > 0 && (
        <div data-testid="pay-rate-history" className="bg-[#0A0A0A] border border-[#262626] rounded p-3">
          <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] font-bold mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Pay Rate History</p>
          <ul className="space-y-1.5">
            {hist.map((h) => (
              <li key={h.id} data-testid={`pay-rate-row-${h.id}`} className="flex items-start justify-between gap-3 text-sm border-b border-[#1a1a1a] last:border-0 pb-1.5 last:pb-0">
                <div className="min-w-0">
                  <span className="text-white font-semibold">{money(h.rate)}/hr</span>
                  {h.is_current && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 uppercase">Current</span>}
                  {h.is_future && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 uppercase">Scheduled</span>}
                  {h.is_opening && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#3f3f3f] text-[#D4D4D4] uppercase">Opening</span>}
                  <p className="text-[#A3A3A3] text-[11px]">
                    {h.effective_date ? `Effective ${fmt(h.effective_date)}` : 'Opening rate'}
                    {h.through_date ? ` – ${fmt(h.through_date)}` : (h.is_current ? ' – Current' : '')}
                  </p>
                  {(h.reason || h.notes) && <p className="text-[#737373] text-[11px] truncate">{[h.reason, h.notes].filter(Boolean).join(' — ')}</p>}
                </div>
                {h.changed_by_name && <span className="text-[#737373] text-[10px] shrink-0 text-right">by {h.changed_by_name}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Change Pay Rate</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">This creates a durable, effective-dated history record — the prior rate is preserved.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white text-xs">Current Rate</Label>
              <Input data-testid="change-rate-current" value={money(data?.current_rate)} disabled className="bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] mt-1" />
            </div>
            <div>
              <Label className="text-white text-xs">New Rate ($/hr)</Label>
              <Input data-testid="change-rate-new" type="number" step="0.01" min="0" value={form.new_rate} onChange={(e) => setForm({ ...form, new_rate: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <div>
              <Label className="text-white text-xs">Effective Date</Label>
              <Input data-testid="change-rate-effective" type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <div>
              <Label className="text-white text-xs">Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="change-rate-reason" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {reasons.map((r) => <SelectItem key={r} value={r} className="text-white">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white text-xs">Notes (optional)</Label>
              <Input data-testid="change-rate-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional context" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
              <Button data-testid="change-rate-save" onClick={save} disabled={saving} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : 'Save Change'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayRateManager;
